// Dotted roads for the map screens (mockup overworld): cream squares with a dark outline every few
// pixels along an orthogonal polyline, in the board's own units (24 per cell). One `<g>` per road;
// callers wrap it for test ids and states (`--dim`, `--lit`).
import type { ReactNode, SVGProps } from 'react';

/** Points every `step` units along the polyline (corners included). */
export function dotsAlong(path: ReadonlyArray<readonly [number, number]>, step: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  let carry = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const [ax, ay] = path[i]!, [bx, by] = path[i + 1]!;
    const len = Math.hypot(bx - ax, by - ay);
    let t = carry;
    while (t <= len) { out.push([ax + ((bx - ax) * t) / (len || 1), ay + ((by - ay) * t) / (len || 1)]); t += step; }
    carry = t - len;
  }
  return out;
}

export function RoadDots({ path, cell = 24, step = 6, size = 3, skip = 0, className = '', children, ...rest }: {
  /** Polyline in cell units (cell centres are x + 0.5). */
  path: ReadonlyArray<readonly [number, number]>; cell?: number; step?: number; size?: number;
  /** Leave this many units clear at each end (under the node icons). */
  skip?: number; className?: string; children?: ReactNode;
} & Omit<SVGProps<SVGGElement>, 'path'>) {
  const pts = dotsAlong(path.map(([x, y]) => [x * cell, y * cell] as const), step);
  const [sx, sy] = [path[0]![0] * cell, path[0]![1] * cell], [ex, ey] = [path[path.length - 1]![0] * cell, path[path.length - 1]![1] * cell];
  const shown = pts.filter(([x, y]) => Math.hypot(x - sx, y - sy) >= skip && Math.hypot(x - ex, y - ey) >= skip);
  const h = size / 2;
  return (
    <g className={`fk-roads__road ${className}`} {...rest}>
      {shown.map(([x, y], i) => <rect key={i} x={Math.round(x - h)} y={Math.round(y - h)} width={size} height={size} className="fk-roads__dot" />)}
      {children}
    </g>
  );
}
